import { observer } from 'mobx-react-lite'
import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router'
import classNames from 'classnames'

// evertel
import { Button, Modal, ModalBody, Row, Col, Text, Icon, ModalHeader, ModalFooter, PeopleSearchSelect, useUI } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { APIDataBlueUser } from '@evertel/types'
import { CreateThreadController } from '@evertel/thread'
import { useInvite } from '@evertel/web/invites'
import { DrawerController } from '@evertel/message'
import { SearchScopeProps } from '@evertel/blue-user'

interface CreateThreadModalProps {
    visible: boolean
    searchScope: SearchScopeProps
    onClose: () => void
}

const USER_LIMIT = 4


const CreateThreadModal: React.FC<CreateThreadModalProps> = observer(({
    visible,
    searchScope = 'contact',
    onClose
}) => {

    const { openInvite } = useInvite()
    const { addToast } = useUI()
    const navigate = useNavigate()

    const sessionService = useService(SessionService, [])
    const createThreadController = useService(CreateThreadController, [])
    const drawerController = useService(DrawerController, [])

    const [isLoading, setIsLoading] = useState(false)
    const [selectedUsers, setSelectedUsers] = useState<APIDataBlueUser[]>([])

    // the current user does not count toward the limit (they are always a participant)
    const countOthers = (users: APIDataBlueUser[]) =>
        users?.filter(u => u.id !== sessionService.currentUserId).length ?? 0

    const othersSelected = countOthers(selectedUsers)
    const atLimit = othersSelected >= USER_LIMIT

    useEffect(() => {
        if (!visible) {
            setSelectedUsers([])
        }
    }, [visible])

    const onSelectChange = (users: APIDataBlueUser[]) => {
        if (countOthers(users) > USER_LIMIT) return

        setSelectedUsers(users)
    }

    const startThread = async () => {
        if (!selectedUsers?.length) return

        setIsLoading(true)

        try {
            const users = selectedUsers.map(u => u.id)

            const thread = await createThreadController.createThread(users as number[])

            // refresh the threads list in the drawer
            await drawerController.fetchActiveThreads()

            // navigate to the new thread and hide modal
            navigate(`/thread/${thread.id}`)
            onClose()
            setSelectedUsers([])

        } catch (error: any) {
            addToast({
                color: 'danger',
                message: error.message
            })
        }

        setIsLoading(false)
    }

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            style={{ minHeight: 'calc(100vh - 4rem)' }}
            size="lg"
        >
            <ModalHeader
                title="Start a Direct Message"
                subtitle={`Add up to ${USER_LIMIT} other people to start a Direct Message`}
            />
            <ModalBody>
                <Row className="me-4">
                    <Col xs={11} valign="top">
                        {(visible) &&
                            <PeopleSearchSelect
                                // searchOnly={true}
                                searchScope={searchScope}
                                selected={selectedUsers || []}
                                whereFilter={{ emailVerified: true }}
                                departmentId={sessionService.selectedDepartmentId}
                                onSelect={onSelectChange}
                                isMulti={true}
                                isSearchable={!atLimit}
                                placeholder="Select one or more people..."
                                formText={
                                    <Text className={classNames({ 'text-warning-emphasis': atLimit })}>
                                        {(atLimit) &&
                                            <Icon
                                                name="circle-info"
                                                className="me-1"
                                                aria-hidden={true}
                                            />
                                        }
                                        {(atLimit)
                                            ? `Maximum of ${USER_LIMIT} people reached — remove someone to add another`
                                            : `${othersSelected} of ${USER_LIMIT} selected`}
                                    </Text>
                                }
                                className={classNames('select-inline search no-bg', {
                                    'select-at-limit': atLimit
                                })}
                                menuIsOpen={true}
                                tabSelectsValue={false}
                                maxMenuHeight="calc(100vh - 370px)"
                            // clearOnSelect
                            />
                        }
                    </Col>
                    <Col
                        valign="top"
                        align="right"
                        className="ms-2">
                        <Button
                            color="primary"
                            block
                            loading={isLoading}
                            disabled={!selectedUsers?.length}
                            onClick={startThread}>
                            <strong>Start</strong>
                        </Button>
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter
                className="p-4">
                <Row>
                    <Col>
                        <Text
                            bold
                            tag="h5">
                            Invite others outside your agency to DM on Evertel.
                        </Text>
                        <Text>
                            Collaboration extends beyond the confines of a single agency. Invite personnel from other agencies,
                            vendors, or any external collaborators to join your secure discussions directly within Evertel.
                        </Text>
                    </Col>
                    <Col
                        xs={3}
                        align="right">
                        <Button
                            color="info"
                            block
                            onClick={() => {
                                onClose()
                                openInvite('contact')
                            }}>
                            Invite People
                        </Button>
                    </Col>
                </Row>
            </ModalFooter>
        </Modal>
    )
})

export { CreateThreadModal }
